import React, {
    useEffect,
    useState
} from "react";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import {
    FaArrowLeft,
    FaExternalLinkAlt,
    FaDownload,
    FaFileAlt,
    FaTrashAlt
} from "react-icons/fa";

import {
    getAdminUserDetails
} from "../api/adminApi";

import { getMediaUrl } from "../../http/media";

import {
    verifyAdminUser,
    rejectAdminUser,
    deleteAdminUser
} from "../api/adminApi";

import AdminRejectModal
    from "../components/AdminRejectModal";

import {
    useConfirmModal
} from "../../components/reusable/ConfirmModalContext";

import "../styles/AdminUserDetails.css";


const AdminUserDetails = () => {

    const { id } = useParams();
    const navigate = useNavigate();

    const {
        openConfirmModal
    } = useConfirmModal();

    const [actionLoading, setActionLoading] =
        useState(false);

    const [rejectModalOpen, setRejectModalOpen] =
        useState(false);

    const [data, setData] =
        useState(null);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");



    const handleVerifyClick = () => {
        const fullName = [
            data?.profile?.firstname,
            data?.profile?.lastname
        ]
            .filter(Boolean)
            .join(" ");

        openConfirmModal({
            title: "Valider le profil",

            message:
                `Confirmer la validation du dossier de ${fullName || "cet utilisateur"
                } ?`,

            confirmText: "Valider",

            cancelText: "Annuler",

            variant: "default",

            onConfirm: async () => {
                try {
                    setActionLoading(true);

                    await verifyAdminUser(id);

                    setData((current) => ({
                        ...current,

                        user: {
                            ...current.user,

                            verification_status:
                                "verified",

                            verification_rejection_reason:
                                null
                        }
                    }));

                } catch (error) {
                    console.error(
                        "VERIFY USER ERROR:",
                        error
                    );

                } finally {
                    setActionLoading(false);
                }
            }
        });
    };


    const handleRejectConfirm = async (
        reason
    ) => {

        try {

            setActionLoading(true);

            await rejectAdminUser(
                id,
                reason
            );

            setData((current) => ({
                ...current,
                user: {
                    ...current.user,
                    verification_status: "rejected",
                    verification_rejection_reason:
                        reason || null
                }
            }));

            setRejectModalOpen(false);

        } catch (error) {

            console.error(
                "REJECT USER ERROR:",
                error
            );

        } finally {

            setActionLoading(false);

        }
    };

    const handleDeleteClick = () => {
        const fullName = [
            data?.profile?.firstname,
            data?.profile?.lastname
        ]
            .filter(Boolean)
            .join(" ") || data?.user?.username || "cet utilisateur";

        openConfirmModal({
            title: "Supprimer définitivement l’utilisateur",
            message:
                `Confirmer la suppression de ${fullName} ? Son compte, ses publications, ses documents et toutes ses données associées seront définitivement supprimés.`,
            confirmText: "Supprimer définitivement",
            cancelText: "Annuler",
            variant: "danger",
            onConfirm: async () => {
                try {
                    setActionLoading(true);
                    await deleteAdminUser(id);
                    navigate("/admin/users", {
                        replace: true
                    });
                } catch (error) {
                    console.error(
                        "DELETE USER ERROR:",
                        error
                    );
                    setError(
                        "La suppression de l’utilisateur a échoué."
                    );
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };


    useEffect(() => {

        const loadUserDetails = async () => {

            try {

                setLoading(true);
                setError("");

                const response =
                    await getAdminUserDetails(id);

                setData(
                    response.data
                );

            } catch (error) {

                console.error(
                    "ADMIN USER DETAILS ERROR:",
                    error
                );

                setError(
                    "Impossible de charger la fiche utilisateur."
                );

            } finally {

                setLoading(false);

            }
        };

        loadUserDetails();

    }, [id]);


    if (loading) {
        return (
            <div className="admin-details-state">
                Chargement de la fiche...
            </div>
        );
    }


    if (error) {
        return (
            <div className="admin-details-error">
                {error}
            </div>
        );
    }


    if (!data) {
        return (
            <div className="admin-details-state">
                Utilisateur introuvable.
            </div>
        );
    }


    const {
        user,
        profile,
        role_details,
        documents
    } = data;


    return (
        <div className="admin-user-details-page">

            <button
                type="button"
                className="admin-details-back"
                onClick={() =>
                    navigate("/admin/users")
                }
            >
                <FaArrowLeft />
                Retour aux utilisateurs
            </button>


            <div className="admin-details-heading">

                <div>
                    <h1>
                        {profile?.firstname || ""}
                        {" "}
                        {profile?.lastname || ""}
                    </h1>

                    <p>
                        @{user?.username}
                    </p>
                </div>

                <StatusBadge
                    status={
                        user?.verification_status
                    }
                />

            </div>


            <Section
                title="Informations personnelles"
            >

                <div className="admin-details-grid">

                    <InfoItem
                        label="Prénom"
                        value={profile?.firstname}
                    />

                    <InfoItem
                        label="Nom"
                        value={profile?.lastname}
                    />

                    <InfoItem
                        label="Email"
                        value={user?.email}
                    />

                    <InfoItem
                        label="Téléphone"
                        value={profile?.phone}
                    />

                    <InfoItem
                        label="Date de naissance"
                        value={
                            formatDate(
                                profile?.birthdate
                            )
                        }
                    />

                    <InfoItem
                        label="Pays"
                        value={profile?.country}
                    />

                    <InfoItem
                        label="Adresse"
                        value={profile?.address}
                    />

                    <InfoItem
                        label="Code postal"
                        value={profile?.postal_code}
                    />

                    <InfoItem
                        label="Rôle"
                        value={
                            getRoleLabel(
                                user?.role
                            )
                        }
                    />

                </div>

            </Section>


            <Section
                title="Informations professionnelles"
            >

                <RoleDetails
                    role={user?.role}
                    details={role_details}
                />

            </Section>


            <Section
                title="Justificatifs"
            >

                {!documents ||
                    documents.length === 0 ? (

                    <div className="admin-documents-empty">
                        Aucun justificatif fourni.
                    </div>

                ) : (

                    <div className="admin-documents-list">

                        {documents.map((document) => {

                            const fileUrl =
                                getMediaUrl(
                                    document.file_name
                                );

                            return (
                                <div
                                    key={document.id}
                                    className="admin-document-card"
                                >

                                    <div className="admin-document-info">

                                        <div className="admin-document-icon">
                                            <FaFileAlt />
                                        </div>

                                        <div>
                                            <strong>
                                                {getDocumentLabel(
                                                    document.document_type
                                                )}
                                            </strong>

                                            <span>
                                                {getFileName(
                                                    document.file_name
                                                )}
                                            </span>
                                        </div>

                                    </div>


                                    <div className="admin-document-actions">

                                        <a
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="admin-document-button"
                                        >
                                            <FaExternalLinkAlt />
                                            Voir
                                        </a>

                                        <a
                                            href={fileUrl}
                                            download
                                            className="admin-document-button"
                                        >
                                            <FaDownload />
                                            Télécharger
                                        </a>

                                    </div>

                                </div>
                            );

                        })}

                    </div>

                )}

            </Section>


            <div className="admin-review-actions">

                <button
                    type="button"
                    className="admin-delete-button"
                    onClick={handleDeleteClick}
                    disabled={actionLoading}
                >
                    <FaTrashAlt />
                    Supprimer
                </button>

                <button
                    type="button"
                    className="admin-reject-button"
                    onClick={() =>
                        setRejectModalOpen(true)
                    }
                    disabled={actionLoading}
                >
                    Rejeter le dossier
                </button>

                <button
                    type="button"
                    className="admin-verify-button"
                    onClick={handleVerifyClick}
                    disabled={actionLoading}
                >
                    {actionLoading
                        ? "Traitement..."
                        : "Valider le profil"}
                </button>

            </div>

            <AdminRejectModal
                isOpen={rejectModalOpen}
                userName={
                    [
                        profile?.firstname,
                        profile?.lastname
                    ]
                        .filter(Boolean)
                        .join(" ")
                }
                loading={actionLoading}
                onClose={() =>
                    setRejectModalOpen(false)
                }
                onConfirm={handleRejectConfirm}
            />

        </div>
    );
};


const Section = ({
    title,
    children
}) => {

    return (
        <section className="admin-details-section">

            <h2>
                {title}
            </h2>

            {children}

        </section>
    );
};


const InfoItem = ({
    label,
    value
}) => {

    return (
        <div className="admin-info-item">

            <span>
                {label}
            </span>

            <strong>
                {displayValue(value)}
            </strong>

        </div>
    );
};


const StatusBadge = ({
    status
}) => {

    const labels = {
        pending: "En cours",
        verified: "Vérifié",
        rejected: "Rejeté"
    };

    return (
        <span
            className={
                `admin-details-status ${status || ""}`
            }
        >
            {labels[status] || status}
        </span>
    );
};


const RoleDetails = ({
    role,
    details
}) => {

    if (
        !details ||
        Object.keys(details).length === 0
    ) {
        return (
            <div className="admin-details-empty">
                Aucune information professionnelle.
            </div>
        );
    }


    const fieldsByRole = {

        athlete: [
            ["sport", "Sport"],
            ["position", "Position"],
            ["level", "Niveau"],
            ["league_name", "Ligue"],
            ["season", "Saison"],
            ["team_name", "Équipe"],
            ["country", "Pays sportif"],
            ["height", "Taille"],
            ["amateur_club", "Club amateur"],
            ["strong_foot", "Pied fort"]
        ],

        staff: [
            ["sport", "Sport"],
            ["status", "Statut"],
            ["position", "Poste / spécialité"],
            ["level", "Niveau"],
            ["licence", "Licence"]
        ],

        lawyer: [
            ["status", "Statut"],
            ["sport", "Sport"]
        ],

        club: [
            ["club_name", "Nom du club"],
            ["legal_form", "Forme juridique"],
            ["legal_id", "Identifiant légal"],
            ["sport", "Sport"],
            ["level", "Niveau"],
            ["league_name", "Ligue"],
            ["season", "Saison"],
            ["country", "Pays"],
            ["amateur_league", "Ligue amateur"],
            ["creation_date", "Date de création"]
        ],

        media: [
            ["type", "Type"],
            ["status", "Statut"],
            ["company_name", "Nom de l'entreprise"]
        ],

        brand: [
            ["legal_name", "Raison sociale"],
            ["brand_name", "Nom de la marque"],
            ["company_name", "Nom de l'entreprise"],
            ["website", "Site web"],
            ["legal_form", "Forme juridique"],
            ["rc_number", "Numéro de registre"],
            ["sector", "Secteur"]
        ],

        fan: [
            ["profession", "Profession"],
            ["status", "Statut"],
            ["category", "Catégorie"]
        ]
    };


    const fields =
        fieldsByRole[role] || [];


    return (
        <div className="admin-details-grid">

            {fields.map(
                ([key, label]) => (

                    <InfoItem
                        key={key}
                        label={label}
                        value={
                            formatRoleValue(
                                key,
                                details[key]
                            )
                        }
                    />

                )
            )}

        </div>
    );
};


const displayValue = (value) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "Non renseigné";
    }

    return String(value);
};


const formatDate = (value) => {

    if (!value) {
        return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString(
        "fr-FR"
    );
};


const formatRoleValue = (
    key,
    value
) => {

    if (!value) {
        return value;
    }

    if (
        key === "creation_date"
    ) {
        return formatDate(value);
    }

    if (
        key === "status" &&
        typeof value === "string" &&
        value.startsWith("[")
    ) {
        try {
            const parsed =
                JSON.parse(value);

            if (Array.isArray(parsed)) {
                return parsed.join(", ");
            }
        } catch {
            return value;
        }
    }

    return value;
};


const getRoleLabel = (role) => {

    const roles = {
        athlete: "Athlète",
        staff: "Staff",
        lawyer: "Avocat / Agent",
        club: "Club",
        media: "Média",
        brand: "Marque",
        fan: "Fan"
    };

    return roles[role] || role;
};


const getDocumentLabel = (type) => {

    const labels = {
        own_id: "Pièce d'identité",
        parent_id: "Pièce d'identité du parent",
        professional_card:
            "Carte professionnelle",

        club_rc:
            "Registre de commerce du club",

        club_statuts:
            "Statuts du club",

        staff_license:
            "Licence du staff",

        staff_certificate:
            "Certificat du staff",

        agent_license_1:
            "Licence agent 1",

        agent_license_2:
            "Licence agent 2",

        agent_license_3:
            "Licence agent 3",

        legal_certificate:
            "Certificat juridique",

        representation_certificate:
            "Certificat de représentation",

        media_press_card:
            "Carte de presse",

        media_proof:
            "Justificatif média",

        media_rc:
            "Registre de commerce média",

        brand_rc:
            "Registre de commerce",

        brand_statuts:
            "Statuts de l'entreprise",

        brand_registration:
            "Enregistrement de la marque"
    };

    return labels[type] || type;
};


const getFileName = (path) => {

    if (!path) {
        return "Fichier";
    }

    return path
        .split("/")
        .pop();
};


export default AdminUserDetails;
